import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import styles from './Sidebar.module.css';

// Legacy SVG icons — kept as fallback for the hardcoded defaults
import DashboardIcon from '../../../../public/assets/icons/dashboard.svg?react';
import ExploreIcon from '../../../../public/assets/icons/explore.svg?react';
import MyCoursesIcon from '../../../../public/assets/icons/courses.svg?react';
import GoalsIcon from '../../../../public/assets/icons/goals.svg?react';
import SparklesIcon from '../../../../public/assets/icons/sparkles.svg?react';
import BookmarkIcon from '../../../../public/assets/icons/bookmark.svg?react';

import { SiteMenuService, SiteMenuItem } from '@/services/site-menu.service';
import { useMultilang } from '@/hooks/useMultilang';
import { useMarkingQueueCount, useOjtEvaluator, useOjtQueueCount } from '@/hooks/useSharedLearnerData';
import { BrandLogo } from '../BrandLogo/BrandLogo';
import {
  SIDEBAR_COLLAPSE_EVENT,
  getSidebarCollapsed,
  toggleSidebarCollapsed,
} from './sidebarCollapse';

import { tr, useLanguage } from '@/i18n';
// ─── Lucide icon renderer ─────────────────────────────────────────────────────

const LucideIcon: React.FC<{ name: string; size?: number }> = ({ name, size = 20 }) => {
  const Icon = (LucideIcons as Record<string, any>)[name];
  if (!Icon) return null;
  return <Icon size={size} />;
};

// ─── Legacy SVG icon map — used when `icon` name matches a known legacy key ──

const LEGACY_ICON_MAP: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  dashboard:    DashboardIcon,
  explore:      ExploreIcon,
  'my-courses': MyCoursesIcon,
  goals:        GoalsIcon,
  sparkles:     SparklesIcon,
  bookmark:     BookmarkIcon,
};

// URL → which legacy icon to use for the hardcoded defaults
const URL_ICON_MAP: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  '/user/dashboard':  DashboardIcon,
  '/user/courses':    ExploreIcon,
  '/user/my-courses': MyCoursesIcon,
  '/user/lea-assistant/recommended-learning': SparklesIcon,
  '/user/goals':  GoalsIcon,
  '/user/skills': GoalsIcon,
  '/user/career': ExploreIcon,
  '/user/mentors': SparklesIcon,
  '/user/team-hub': GoalsIcon,
  '/user/teaching': MyCoursesIcon,
  '/user/forms':  GoalsIcon,
  '/user/saved':  BookmarkIcon,
};

// ─── Hardcoded fallback (shown while API loads or if API is empty) ────────────
// Built per render (not at module load) so the labels follow the active language.

const fallbackItems = (): SiteMenuItem[] => [
  { id: -1, tenant_id: null, label: tr("breadcrumb.dashboard", "Dashboard"),    icon: null, url: '/user/dashboard',  tooltip: tr("breadcrumb.dashboard", "Dashboard"),    sort_order: 0, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -2, tenant_id: null, label: tr("ui.explore", "Explore"),      icon: null, url: '/user/courses',    tooltip: tr("ui.explore", "Explore"),      sort_order: 1, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -3, tenant_id: null, label: tr("ui.my_learning", "My Learning"),  icon: null, url: '/user/my-courses', tooltip: tr("ui.my_learning_e7f108", "My learning"),  sort_order: 2, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -4, tenant_id: null, label: tr("ui.lea_assistant", "Lea Assistant"),icon: null, url: '/user/lea-assistant/recommended-learning', tooltip: tr("ui.lea_assistant_2cf26a", "Lea assistant"), sort_order: 3, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -5, tenant_id: null, label: tr("ui.goals", "Goals"),        icon: null, url: '/user/goals',      tooltip: tr("ui.goals", "Goals"),        sort_order: 4, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -8, tenant_id: null, label: tr("course.tabs.skills", "Skills"),       icon: null, url: '/user/skills',     tooltip: tr("ui.my_skills_e8f13d", "My skills"), sort_order: 5, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -9, tenant_id: null, label: tr("ui.career", "Career"),       icon: null, url: '/user/career',     tooltip: tr("ui.career_path", "Career path"), sort_order: 5, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -10, tenant_id: null, label: tr("ui.mentors", "Mentors"),     icon: null, url: '/user/mentors',    tooltip: tr("ui.find_a_mentor_ff67ee", "Find a mentor"), sort_order: 5, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -11, tenant_id: null, label: tr("widgets.my_team_name", "My Team"),     icon: null, url: '/user/team-hub',   tooltip: tr("ui.my_team", "My team"), sort_order: 5, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -12, tenant_id: null, label: tr("ui.teaching", "Teaching"),    icon: null, url: '/user/teaching',   tooltip: tr("ui.my_teaching", "My teaching"), sort_order: 5, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -7, tenant_id: null, label: tr("nav.forms", "Forms"),        icon: null, url: '/user/forms',      tooltip: tr("ui.my_forms", "My forms"),     sort_order: 6, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -13, tenant_id: null, label: tr("myreports.title", "My reports"), icon: 'BarChart3', url: '/user/reports', tooltip: tr("myreports.title", "My reports"), sort_order: 6, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -6, tenant_id: null, label: tr("ui.saved", "Saved"),        icon: null, url: '/user/saved',      tooltip: tr("ui.saved_learning_b70c6f", "Saved learning"),sort_order: 6, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
];

// ─── Sidebar component ────────────────────────────────────────────────────────

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const { ml } = useMultilang(); // labels/tooltips are multilang strings
  const { strings } = useLanguage();
  const [loadedItems, setItems] = useState<SiteMenuItem[] | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `strings` re-resolves the fallback labels
  const items = useMemo(() => loadedItems ?? fallbackItems(), [loadedItems, strings]);
  const [, setLoaded] = useState(false);

  // Collapsed = icon-only rail; expanded = icons + labels. Persisted per user,
  // toggled from the header chevron (synced via a window event). On mobile the
  // drawer always shows labels regardless of this (handled in CSS).
  const [collapsed, setCollapsed] = useState<boolean>(getSidebarCollapsed);

  // Collapsed-rail hover tooltip. Rendered in a PORTAL on document.body with
  // fixed positioning so the scrollable nav's overflow can't clip it.
  const [tip, setTip] = useState<{ text: string; top: number; left: number } | null>(null);
  const tipProps = (text: string) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      if (!collapsed) return;
      const r = e.currentTarget.getBoundingClientRect();
      setTip({ text, top: r.top + r.height / 2, left: r.right + 12 });
    },
    onMouseLeave: () => setTip(null),
  });

  useEffect(() => {
    const onCollapse = (e: Event) => {
      const detail = (e as CustomEvent).detail as { collapsed?: boolean } | undefined;
      setCollapsed(detail?.collapsed ?? getSidebarCollapsed());
    };
    window.addEventListener(SIDEBAR_COLLAPSE_EVENT, onCollapse);
    return () => window.removeEventListener(SIDEBAR_COLLAPSE_EVENT, onCollapse);
  }, []);

  useEffect(() => {
    let cancelled = false;
    SiteMenuService.getUserMenu()
      .then(res => {
        if (cancelled) return;
        // If the DB has items configured, use them; otherwise keep fallback
        if (res.results && res.results.length > 0) {
          setItems(res.results);
        }
        setLoaded(true);
      })
      .catch(() => {
        // API failed — silently keep the fallback hardcoded list
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Essay-marking and OJT evaluation queue counts (> 0 only for markers / SMEs — the endpoints are data-scoped)
  // and whether I evaluate any OJT (the link shows even with nothing waiting). Shared with the Tasks widget
  // and the top bar: one request each per page.
  const markCount = useMarkingQueueCount().data ?? 0;
  const ojtCount = useOjtQueueCount().data ?? 0;
  const ojtEvaluator = useOjtEvaluator().data ?? false;

  // ── Parent groups: an item with NO url is a group; children reference it
  //    via parent_id. Groups with no visible children are hidden. ──────────
  const entries = useMemo(() => {
    const childrenByParent = new Map<number, SiteMenuItem[]>();
    items.forEach(i => {
      if (i.parent_id != null && i.url) {
        if (!childrenByParent.has(i.parent_id)) childrenByParent.set(i.parent_id, []);
        childrenByParent.get(i.parent_id)!.push(i);
      }
    });
    const out: Array<
      | { kind: 'link'; item: SiteMenuItem }
      | { kind: 'group'; item: SiteMenuItem; children: SiteMenuItem[] }
    > = [];
    items.forEach(i => {
      if (i.parent_id != null && i.url) return; // rendered under its parent
      if (!i.url) {
        const kids = childrenByParent.get(i.id) ?? [];
        if (kids.length) out.push({ kind: 'group', item: i, children: kids });
        return;
      }
      out.push({ kind: 'link', item: i });
    });
    return out;
  }, [items]);

  // Open/closed groups — the group holding the active route auto-opens.
  const [openGroups, setOpenGroups] = useState<Set<number>>(new Set());
  useEffect(() => {
    entries.forEach(e => {
      if (e.kind === 'group' && e.children.some(c => c.url && location.pathname.startsWith(c.url))) {
        setOpenGroups(prev => (prev.has(e.item.id) ? prev : new Set(prev).add(e.item.id)));
      }
    });
  }, [entries, location.pathname]);

  const toggleGroup = (id: number) =>
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      {/* Edge collapse toggle — revealed when the pointer is over the rail. */}
      <button
        type="button"
        className={styles.edgeCollapseBtn}
        onClick={toggleSidebarCollapsed}
        aria-label={collapsed ? tr("ui.expand_menu", "Expand menu") : tr("ui.collapse_menu", "Collapse menu")}
        title={collapsed ? tr("ui.expand_menu", "Expand menu") : tr("ui.collapse_menu", "Collapse menu")}
      >
        {collapsed
          ? <LucideIcons.ArrowRightToLine size={16} aria-hidden="true" />
          : <LucideIcons.ArrowLeftToLine size={16} aria-hidden="true" />}
      </button>
      <div className={styles.logoRow}>
        {/* CSS swaps these: wordmark when expanded (and in the mobile drawer),
            compact square mark when collapsed to the icon rail. */}
        <BrandLogo className={styles.sbLogo} />
        <BrandLogo className={styles.sbLogoMark} variant="mark" />
      </div>

      <nav className={styles.nav}>
        {/* SVG gradient defs — still needed for legacy Sparkles icon */}
        <svg width="0" height="0" style={{ position: 'absolute', visibility: 'hidden' }} aria-hidden="true">
          <defs>
            <linearGradient id="sparklesGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#B692FF" />
              <stop offset="100%" stopColor="#DEB7FF" />
            </linearGradient>
          </defs>
        </svg>

        {entries.map(entry => {
          const renderLink = (item: SiteMenuItem) => {
            const url = item.url!;
            const LegacyIcon = URL_ICON_MAP[url] ?? (item.icon ? LEGACY_ICON_MAP[item.icon.toLowerCase()] : null);
            const isSparkles = url.includes('lea-assistant') || item.icon === 'Sparkles';
            const isActive = isSparkles
              ? location.pathname.startsWith('/user/lea-assistant')
              : undefined; // let NavLink handle it

            const linkContent = (
              <>
                <span className={styles.navIcon}>
                  {LegacyIcon ? (
                    <LegacyIcon aria-hidden="true" />
                  ) : item.icon ? (
                    <LucideIcon name={item.icon} size={20} />
                  ) : (
                    <LucideIcon name="Link" size={20} />
                  )}
                </span>
                <span className={styles.label}>{ml(item.label)}</span>
              </>
            );

            if (item.open_in_new_tab) {
              return (
                <div key={item.id} className={styles.navItemWrapper} {...tipProps(ml(item.tooltip || item.label))}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${isSparkles ? styles.navItem2 : styles.navItem}`}
                    aria-label={ml(item.tooltip || item.label)}
                  >
                    {linkContent}
                  </a>
                </div>
              );
            }

            return (
              <div key={item.id} className={styles.navItemWrapper} {...tipProps(ml(item.tooltip || item.label))}>
                {isSparkles ? (
                  <NavLink
                    to={url}
                    className={`${styles.navItem2} ${isActive ? styles.active : ''}`}
                    aria-label={ml(item.tooltip || item.label)}
                  >
                    {linkContent}
                  </NavLink>
                ) : (
                  <NavLink
                    to={url}
                    className={({ isActive: a }) => `${styles.navItem} ${a ? styles.active : ''}`}
                    aria-label={ml(item.tooltip || item.label)}
                  >
                    {linkContent}
                  </NavLink>
                )}
              </div>
            );
          };

          if (entry.kind === 'link') return renderLink(entry.item);

          // ── Parent group: not a link — clicking expands its children. ──
          const group = entry.item;
          const isOpen = openGroups.has(group.id);
          const hasActiveChild = entry.children.some(
            c => c.url && location.pathname.startsWith(c.url),
          );
          return (
            <div key={`group-${group.id}`} className={styles.groupWrap}>
              <div className={styles.navItemWrapper} {...tipProps(ml(group.tooltip || group.label))}>
                <button
                  type="button"
                  className={`${styles.navItem} ${styles.groupBtn} ${hasActiveChild ? styles.groupBtnActive : ''}`}
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={isOpen}
                  aria-label={ml(group.tooltip || group.label)}
                >
                  <span className={styles.navIcon}>
                    {group.icon ? (
                      <LucideIcon name={group.icon} size={20} />
                    ) : (
                      <LucideIcon name="Folder" size={20} />
                    )}
                  </span>
                  <span className={styles.label}>{ml(group.label)}</span>
                  <LucideIcons.ChevronDown
                    size={15}
                    className={`${styles.groupChevron} ${isOpen ? styles.groupChevronOpen : ''}`}
                    aria-hidden="true"
                  />
                </button>
              </div>
              {isOpen && (
                <div className={styles.groupChildren}>
                  {entry.children.map(c => renderLink(c))}
                </div>
              )}
            </div>
          );
        })}

        {/* Essay marking — shown only to users with assigned essays to grade */}
        {markCount > 0 && (
          <div className={styles.navItemWrapper} {...tipProps(`${tr("ui.essay_marking", "Essay marking")} (${markCount})`)}>
            <NavLink
              to="/user/marking"
              className={({ isActive: a }) => `${styles.navItem} ${a ? styles.active : ''}`}
              aria-label={tr("ui.essay_marking", "Essay marking")}
            >
              <span className={styles.navIcon}>
                <LucideIcon name="ClipboardCheck" size={20} />
              </span>
              <span className={styles.label}>{tr("ui.essay_marking", "Essay marking")}</span>
              <span className={styles.countBadge}>{markCount > 99 ? '99+' : markCount}</span>
            </NavLink>
          </div>
        )}

        {/* OJT evaluations — for people who evaluate OJT (course SMEs, named supervisors); badge = waiting */}
        {(ojtCount > 0 || ojtEvaluator) && (
          <div className={styles.navItemWrapper} {...tipProps(ojtCount > 0 ? `${tr("ui.ojt_evaluations", "OJT evaluations")} (${ojtCount})` : tr("ui.ojt_evaluations", "OJT evaluations"))}>
            <NavLink
              to="/user/ojt-evaluations"
              className={({ isActive: a }) => `${styles.navItem} ${a ? styles.active : ''}`}
              aria-label={tr("ui.ojt_evaluations", "OJT evaluations")}
            >
              <span className={styles.navIcon}>
                <LucideIcon name="ClipboardList" size={20} />
              </span>
              <span className={styles.label}>{tr("ui.ojt_evaluations", "OJT evaluations")}</span>
              {ojtCount > 0 && <span className={styles.countBadge}>{ojtCount > 99 ? '99+' : ojtCount}</span>}
            </NavLink>
          </div>
        )}
      </nav>

      {/* Portal tooltip — escapes the scrollable nav's clipping. */}
      {collapsed && tip && createPortal(
        <div
          className={styles.floatTip}
          style={{ top: tip.top, left: tip.left }}
          role="tooltip"
        >
          {tip.text}
        </div>,
        document.body,
      )}
    </aside>
  );
};